import { useCallback, useMemo } from 'react';
import { useLazyGetBundleSectionsQuery } from '@/api';
import {
  makeSelectBundlePageIdByPageIndex,
  selectBundleFilterOptions,
  selectEnabledStates,
  selectSelectedValues,
  selectSharedDataActiveBundleType,
  selectSharedDataBundleId,
  selectViewportPageIndexInStore,
  setEnabledStates,
  setSelectedValues,
  setViewportPageIdForRestorePageIndexInStore,
  useAppDispatch,
  useAppSelector,
} from '@/store';
import {
  getBundleSectionsBody,
  getDateRangeLabel,
  getSortedBundleFilterOptions,
  isProvidersFilter,
} from '@/ui/pages/BundlePage/utils';
import {
  FILTER_BLANKS_KEY,
  FILTER_NON_BLANKS_KEY,
  FILTER_EXTRACTED_KEY,
  FILTER_DUPLICATES_KEY,
} from '@/constants/filters';

type ChipItem = {
  key: string;
  label: string;
  onRemove: () => boolean;
};

export const useBundleFilterChips = () => {
  const [triggerBundleSections] = useLazyGetBundleSectionsQuery();

  const bundleId = useAppSelector(selectSharedDataBundleId);
  const activeBundleType = useAppSelector(selectSharedDataActiveBundleType);
  const bundleFilterOptions = useAppSelector(selectBundleFilterOptions);
  const selectedValues = useAppSelector(selectSelectedValues);
  const enabledStates = useAppSelector(selectEnabledStates);

  const viewportPageIndexInStore = useAppSelector(selectViewportPageIndexInStore);
  const getBundlePageIdByPageIndex = useAppSelector(makeSelectBundlePageIdByPageIndex);

  const dispatch = useAppDispatch();

  const sortedBundleFilterOptions = useMemo(
    () => getSortedBundleFilterOptions(bundleFilterOptions),
    [bundleFilterOptions],
  );

  const chipsList: ChipItem[] = useMemo(
    () => {
      const dynamicChips = sortedBundleFilterOptions.reduce((acc, filterOption) => {
        if (!bundleId || !activeBundleType) {
          return acc;
        }

        if (filterOption.displayType === 'dates' && enabledStates[filterOption.groupName]) {
          return [
            ...acc,
            {
              key: filterOption.groupName,
              label: getDateRangeLabel(selectedValues[filterOption.groupName] as Date[] | null),
              onRemove: () => {
                const newEnabledStates = {
                  ...enabledStates,
                  [filterOption.groupName]: false,
                };

                const newSelectedValues = {
                  ...selectedValues,
                  [filterOption.groupName]: [],
                };

                const bundleSectionsBody = getBundleSectionsBody({
                  bundleId,
                  selectedValues: newSelectedValues,
                  enabledStates: newEnabledStates,
                  bundleFilterOptions: sortedBundleFilterOptions,
                });

                triggerBundleSections(bundleSectionsBody);

                dispatch(
                  setEnabledStates({
                    key: activeBundleType,
                    data: newEnabledStates,
                  }),
                );

                dispatch(
                  setSelectedValues({
                    key: activeBundleType,
                    data: newSelectedValues,
                  }),
                );

                /** Primereact requires to return false to prevent hiding the chip after removal */
                return false;
              },
            },
          ];
        }

        if (filterOption.displayType === 'drop_down' && enabledStates[filterOption.groupName]) {
          const isProviders = isProvidersFilter(filterOption);

          return [
            ...acc,
            ...filterOption.options
              .filter((item: { id: string; value: string; providerId?: string; providerName?: string }) => {
                const idsList = selectedValues[filterOption.groupName] as string[] | undefined;
                return idsList?.includes(isProviders ? (item as { providerId: string }).providerId : item.id);
              })
              .map((item: { id: string; value: string; providerId?: string; providerName?: string }) => ({
                key: isProviders ? (item as { providerId: string }).providerId : item.id,
                label: isProviders ? (item as { providerName: string }).providerName : item.value,
                onRemove: () => {
                  const newSelectedValues = {
                    ...selectedValues,
                    [filterOption.groupName]: (selectedValues[filterOption.groupName] as string[]).filter(
                      (id) => id !== (isProviders ? item.providerId : item.id),
                    ),
                  };

                  const bundleSectionsBody = getBundleSectionsBody({
                    bundleId,
                    selectedValues: newSelectedValues,
                    enabledStates,
                    bundleFilterOptions: sortedBundleFilterOptions,
                  });

                  triggerBundleSections(bundleSectionsBody);

                  dispatch(
                    setSelectedValues({
                      key: activeBundleType,
                      data: newSelectedValues,
                    }),
                  );

                  /** Primereact requires to return false to prevent hiding the chip after removal */
                  return false;
                },
              })),
          ];
        }

        if (filterOption.displayType === 'checkbox') {
          return [
            ...acc,
            ...filterOption.options
              .filter((item) => {
                const idsList = selectedValues[filterOption.groupName] as string[] | undefined;
                return idsList?.includes(item.id);
              })
              .map((item) => ({
                key: item.id,
                label: item.value,
                onRemove: () => {
                  const newSelectedValues = {
                    ...selectedValues,
                    [filterOption.groupName]: (selectedValues[filterOption.groupName] as string[]).filter(
                      (id) => id !== item.id,
                    ),
                  };

                  const bundleSectionsBody = getBundleSectionsBody({
                    bundleId,
                    enabledStates,
                    selectedValues: newSelectedValues,
                    bundleFilterOptions: sortedBundleFilterOptions,
                  });

                  triggerBundleSections(bundleSectionsBody);

                  dispatch(
                    setSelectedValues({
                      key: activeBundleType,
                      data: newSelectedValues,
                    }),
                  );

                  /** Primereact requires to return false to prevent hiding the chip after removal */
                  return false;
                },
              })),
          ];
        }

        return acc;
      }, [] as ChipItem[]);

      // Add static boolean filter chips. Order mirrors the filter dialog.
      const staticFilterDefs = [
        { key: FILTER_BLANKS_KEY, label: 'Include blank pages' },
        { key: FILTER_NON_BLANKS_KEY, label: 'Show only blank pages' },
        { key: FILTER_DUPLICATES_KEY, label: 'Hide duplicate pages' },
        { key: FILTER_EXTRACTED_KEY, label: 'Hide extracted pages' },
      ];

      for (const sf of staticFilterDefs) {
        if (enabledStates[sf.key] && bundleId && activeBundleType) {
          dynamicChips.push({
            key: sf.key,
            label: sf.label,
            onRemove: () => {
              if (!bundleId || !activeBundleType) return false;
              const newEnabledStates = { ...enabledStates, [sf.key]: false };

              const bundleSectionsBody = getBundleSectionsBody({
                bundleId,
                selectedValues,
                enabledStates: newEnabledStates,
                bundleFilterOptions: sortedBundleFilterOptions,
              });

              triggerBundleSections(bundleSectionsBody);
              dispatch(setEnabledStates({ key: activeBundleType, data: newEnabledStates }));

              return false;
            },
          });
        }
      }

      return dynamicChips;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeBundleType, bundleId, enabledStates, selectedValues, sortedBundleFilterOptions],
  );

  const handleClearAll = useCallback(() => {
    if (!bundleId || !activeBundleType) {
      return;
    }

    const newSelectedValues = { ...selectedValues };
    Object.keys(newSelectedValues).forEach((key) => {
      if (Array.isArray(newSelectedValues[key])) {
        newSelectedValues[key] = [];
      }
    });

    const newEnabledStates = { ...enabledStates };
    Object.keys(newEnabledStates).forEach((key) => {
      newEnabledStates[key] = false;
    });

    const bundleSectionsBody = getBundleSectionsBody({
      bundleId,
      selectedValues: newSelectedValues,
      enabledStates: newEnabledStates,
      bundleFilterOptions: sortedBundleFilterOptions,
    });

    const currentPageId = getBundlePageIdByPageIndex(viewportPageIndexInStore);
    dispatch(setViewportPageIdForRestorePageIndexInStore(currentPageId));

    triggerBundleSections(bundleSectionsBody);

    dispatch(setSelectedValues({ key: activeBundleType, data: newSelectedValues }));
    dispatch(setEnabledStates({ key: activeBundleType, data: newEnabledStates }));
  }, [
    activeBundleType,
    bundleId,
    dispatch,
    enabledStates,
    getBundlePageIdByPageIndex,
    selectedValues,
    sortedBundleFilterOptions,
    triggerBundleSections,
    viewportPageIndexInStore,
  ]);

  return { chipsList, handleClearAll };
};
